import React from "react";
import ct from "class-types.macro";
import { BranchCategoryDetail, BrandSpotlightAdResult, FeaturedCategory } from "~/common/Store/models/Category";
import PhotoGridDisplay, { PhotoGridItem } from "~/common/PhotoGridDisplay";
import PhotoGridHeader from "./PhotoGridHeader";
import RelatedCategoryCarousel from "../RelatedCategoryCarousel";
import Resources from "./Resources";
import getSeoCategoryImageUrl from "~/CategoryPage/getSeoCategoryImageUrl";
import TopProductsCarousel from "~/common/TopProductsCarousel/TopProductsCarousel";
import Breadcrumbs from "~/common/Breadcrumbs";
import BrandSpotlightAd from "./BrandSpotlightAd";
import useIsMounted from "~/common/useIsMounted";
import Store, { Provider } from "~/common/Store";
import { defaultUserObject } from "~/common/Store/rootReducer";
import EnhancedCarousel from "./EnhancedCarousel";
import CarouselSchema from "./CarouselSchema";
import { usePageTracking } from "~/analytics/usePageTracking";
const PhotoGridCategory = (detail: BranchCategoryDetail): JSX.Element => {
  const isMounted = useIsMounted();
  usePageTracking("CategoryPageEvent");
  const getFeaturesPerRow = (category: FeaturedCategory): number => {
    return category.isGridSplit ? 4 : 3;
  };
  const getPhotoGridItem = (category: FeaturedCategory): PhotoGridItem => {
    return {
      description: category.description,
      image: getSeoCategoryImageUrl(category.image),
      url: category.url,
      name: category.name,
      actionText: category.childCount > 0 ? `${detail.seoCategoryProperties.actionWord ? detail.seoCategoryProperties.actionWord : "Shop"} ${category.childCount} ${category.childType}` : "",
      featuresPerRow: getFeaturesPerRow(category),
      children: category.featuredGrandchildren?.map(grandChild => ({
        name: grandChild.name,
        url: grandChild.url
      }))
    };
  };
  const categoryTopProductDetails = detail.categoryTopProducts?.length ? detail.categoryTopProducts.flatMap(topProduct => topProduct.product) : [];
  const categories: (FeaturedCategory[] | FeaturedCategory | BrandSpotlightAdResult | null)[] = [];
  let photoGrid: FeaturedCategory[] = [];
  detail?.featuredChildren?.forEach(child => {
    if (child.isEnhancedGrid && child.enhancedGrid) {
      if (!child.enhancedGrid.sections.some(s => s.description)) {
        return;
      }
      categories.push(child);
      photoGrid = [];
    } else {
      photoGrid.push(child);
      if (photoGrid.length === getFeaturesPerRow(child)) {
        categories.push(photoGrid);
        categories.push(null);
        photoGrid = [];
      }
    }
  });
  categories.splice(1, 0, detail.brandSpotlightAd);
  const {
    adId,
    brandIds,
    brandName,
    link,
    images,
    enhancedGrid
  } = {
    ...detail.brandSpotlightAd
  };
  return <Provider store={Store} data-sentry-element="Provider" data-sentry-component="PhotoGridCategory" data-sentry-source-file="index.tsx">
      <Breadcrumbs breadcrumbs={detail.layoutConfiguration.breadCrumb.items} olClassName="breadcrumb--inverse breadcrumb--hero breadcrumb--absolute" data-sentry-element="Breadcrumbs" data-sentry-source-file="index.tsx" />
      <PhotoGridHeader categoryId={detail.id} heading={detail.name} subHeading={detail.seoCategoryProperties?.seoHeadline} image={detail.headerImage} data-sentry-element="PhotoGridHeader" data-sentry-source-file="index.tsx" />
      <div className="container" data-module="customCategory">
        <div id="page">
          <div id="main" className="skip-to-main-target">
            <div className="grid-layout grid-layout--tuck overflow-x-hidden">
              <div className="grid-container grid-container--compact grid-module-large clearfix">
                <div className="grid-module cols-grid grid-module--wrap grid-module--collapse">
                  <div className={ct("border-gray-200", "border-solid", "border-r-0", "border-b-0", "border-l-0", "border-t", "block", "-my-px", "w-full", "xs:border-t-0")}>
                    {categories.map(child => {
                    if (child instanceof Array) {
                      return <div className={ct("border-gray-200", "border-solid", "border", "box-border", "flex", "flex-wrap", "-mt-px", "-mr-1/2", "overflow-hidden", "xs:border-0", "xs:-mr-1-1/2")} data-testid="Photo Grid Categories">
                            {child.map(item => {
                          return <div className={`${ct("border-gray-200", "border-solid", "border-t-0", "border-r-0", "border-l-0", "border-b", "box-border", "items-center", "justify-center", "-mb-px", "-ml-px", "p-5", "xs:border-0", "xs:even:border-0", "xs:odd:border-0", "xs:p-3", item.isGridSplit ? "lz:w-1/4" : "lz:w-1/3", child.length % 2 !== 0 ? ct("even:border-r", "first:p-6", "first:w-full", "xs:p-3", "xs:first:p-3", "xs:first:w-1/2", item.isGridSplit ? "lz:first:w-1/4" : "lz:first:w-1/3") : "odd:border-r")} ${ct("w-1/2")}
                                    `}>
                                  <PhotoGridDisplay item={getPhotoGridItem(item)} showActionButton={detail.seoCategoryProperties?.showActionButtons} key={item.name} />
                                </div>;
                        })}
                          </div>;
                    }
                    if (detail?.brandSpotlightAd && child && "brandIds" in child) {
                      return <Provider key={adId} store={Store}>
                            <BrandSpotlightAd adId={adId || -1} brandIds={brandIds} brandName={brandName} link={link} images={images} enhancedGrid={enhancedGrid} mounted={isMounted} user={defaultUserObject} />
                          </Provider>;
                    }
                    if (child && "categoryId" in child) {
                      return <EnhancedCarousel enhancedGridResult={child.enhancedGrid} key={child.name} user={defaultUserObject} mounted={isMounted} mainCategoryId={detail.id} />;
                    }
                    return <></>;
                  })}
                  </div>
                  {!!categoryTopProductDetails?.length && <div className={ct("mx-0", "-mb-px", "xs:mb-0", "md:mx-3", "md:mb-3")}>
                      <TopProductsCarousel topProducts={categoryTopProductDetails} productListingView="topProductsCarousel" itemWidth={ct("w-1/2", "xsl:w-48")} pageType="Category" />
                      <CarouselSchema products={categoryTopProductDetails} />
                    </div>}
                </div>
                {detail.relatedCategories && detail.relatedCategories.length > 0 && <div className="other-categories">
                      <RelatedCategoryCarousel categoryName={detail.name} parentCategoryName={detail.parent?.name} relatedCategories={detail.relatedCategories} />
                    </div>}
              </div>
            </div>
            <Resources {...detail} data-sentry-element="Resources" data-sentry-source-file="index.tsx" />
          </div>
        </div>
      </div>
    </Provider>;
};
export default PhotoGridCategory;